import { Component, Input } from '@angular/core';
import { ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { shakeAnimation } from '@freelancer/animations';
import { ButtonComponent } from '@freelancer/ui/button';
import { IconModule } from '@freelancer/ui/icon';
import { ValidationErrorComponent } from '@freelancer/ui/validation-error';
import type { IsAny } from '@freelancer/utils';
import { InputBaseComponent } from './input-base.component';
import { InputTextSize, InputType } from './input.types';
import { NumberValueAccessorDirective } from './value-accessors/number-value-accessor.directive';

@Component({
  standalone: true,
  selector: 'fl-input',
  imports: [
    ButtonComponent,
    IconModule,
    NumberValueAccessorDirective,
    ReactiveFormsModule,
    ValidationErrorComponent,
  ],
  template: `
    <div
      class="InputInner"
      [attr.data-type]="type"
      [attr.data-align]="align"
      [attr.data-edit-inline]="editInline"
      [attr.data-background-color]="backgroundColor"
    >
      @if (isNumberIncrement) {
        <fl-button
          i18n="Input increment button"
          class="InputIncrement"
          [color]="ButtonColor.CUSTOM"
          [disabled]="
            attrDisabled === true ||
            attrReadonly === true ||
            shouldDisableDecrementButton()
          "
          [size]="buttonIncrementSize"
          [attr.data-invalid]="hasError && !isWarning"
          [attr.data-valid]="isValid"
          [attr.data-warning]="isWarning"
          (click)="handleMinus()"
        >
          <p>-</p>
        </fl-button>
      }
      <div
        #inputContainer
        class="InputContainer"
        [class.BeforeLabelPresent]="beforeLabel"
        [class.AfterLabelPresent]="afterLabel"
        [class.Shadowed]="isShadowed"
        [class.IsFocused]="inputFocused === true"
        [class.IsExpandable]="isExpandable === true"
        [class.ExpandFromRight]="
          expandFromDirection === SearchExpandFromDirection.RIGHT
        "
        [class.IsBorderless]="borderless === true"
        [class.IsBorderlessMobile]="borderlessMobile === true"
        [class.SearchInput]="type === InputType.SEARCH"
        [class.SearchInput-roundCorners]="
          type === InputType.SEARCH && searchRoundCorners
        "
        [class.IsInvalid]="hasError && !isWarning"
        [class.IsValid]="isValid === true"
        [class.CenterAlign]="shouldCenterAlign"
        [attr.data-size]="size"
        [attr.data-size-tablet]="sizeTablet"
        [attr.data-text-size]="textSize"
        [attr.data-size-desktop]="sizeDesktop"
        [attr.disabled]="attrDisabled"
        [attr.data-expanded]="expanded"
        [attr.data-edit-inline]="editInline"
        [attr.data-hide-icon]="
          inputFocused || (isString(control.value) && control.value?.length > 0)
        "
        [attr.data-transparent-background]="transparentBackground"
        [@shakeAnimation]="{
          value: shakeState,
          params: {
            duration: 200,
            translateX: '3px',
          },
        }"
        (@shakeAnimation.done)="shakeDone()"
      >
        @if (beforeLabel) {
          <div
            class="InputLabel BeforeLabel"
            [attr.data-size]="size"
          >
            <span class="LabelText"> {{ beforeLabel }} </span>
          </div>
        }
        @if (iconStartClickable) {
          <fl-button
            [class]="
              iconMarginMobile
                ? 'InputButtonStart'
                : 'InputButtonStart-noMarginMobile'
            "
            [label]="iconStartLabel"
            (click)="handleIconStartClick($event)"
          >
            @if (iconStart) {
              <fl-icon
                [size]="leftIconSize"
                [color]="leftIconColor"
                [hoverColor]="HoverColor.PRIMARY"
                [name]="iconStart"
                [useIconFont]="true"
                [fillIconFont]="true"
              ></fl-icon>
            }
          </fl-button>
        }
        @if (iconStart && !iconStartClickable) {
          <fl-icon
            [class]="
              iconMarginMobile
                ? 'IconElementStart'
                : 'IconElementStart-noMarginMobile'
            "
            [size]="leftIconSize"
            [color]="leftIconColor"
            [label]="iconStartLabel"
            [name]="iconStart"
            [useIconFont]="true"
            [fillIconFont]="true"
          ></fl-icon>
        }
        <div
          class="NativeElementContainer"
          [attr.data-background-color]="backgroundColor"
          [attr.data-transparent-background]="transparentBackground"
          [attr.data-edit-inline]="editInline"
          [attr.disabled]="attrDisabled"
        >
          @if (isNumberInput) {
            <!--
            FIXME: T276037 - This is separate because [attr.type] binding with 'number'
            returns 'string' instead of 'number': https://github.com/angular/angular/issues/13243
            -->
            <input
              #nativeElement
              class="NativeElement"
              [class.HasIconStart]="iconStart"
              [class.HasIconEnd]="iconEnd"
              type="number"
              [attr.controlType]="controlType"
              [attr.id]="id"
              [attr.step]="type === InputType.CURRENCY ? 0.01 : null"
              [attr.placeholder]="
                editInline !== InputEditInlineType.BORDERED || inputFocused
                  ? placeholder
                  : ''
              "
              [attr.readonly]="attrReadonly ? true : null"
              [attr.disabled]="attrDisabled"
              [attr.maxlength]="maxLength ? maxLength : null"
              [attr.data-align]="textAlign"
              [attr.data-text-color]="color"
              [attr.autocomplete]="autoComplete"
              [attr.data-size]="size"
              [attr.data-left-icon-size]="leftIconSize"
              [attr.data-right-icon-size]="rightIconSize"
              [attr.aria-errormessage]="errorId"
              [attr.aria-invalid]="hasError && !isWarning"
              [attr.aria-label]="label"
              [attr.data-borderless]="borderless"
              [attr.data-borderlessMobile]="borderlessMobile"
              [attr.data-custom-arrows]="
                isNumberIncrement || type === InputType.NUMBER_TEXT
              "
              [attr.data-edit-inline]="editInline"
              [attr.min]="minValue"
              [attr.data-transparent-background]="transparentBackground"
              [attr.inputmode]="
                type === InputType.CURRENCY ? 'decimal' : keyboardType
              "
              [formControl]="control"
              (focus)="handleFocus()"
              (blur)="handleBlur()"
              (keyup)="handleKeyUp()"
              (wheel)="handleInputWheel()"
              (change)="handleOnChange()"
            />
          } @else {
            <input
              #nativeElement
              class="NativeElement"
              [class.HasIconStart]="iconStart"
              [class.HasIconEnd]="iconEnd"
              [attr.id]="id"
              [attr.type]="inputType"
              [attr.step]="type === InputType.CURRENCY ? 0.01 : null"
              [attr.placeholder]="
                editInline !== InputEditInlineType.BORDERED || inputFocused
                  ? getPlaceholderWithCursor()
                  : ''
              "
              [attr.readonly]="attrReadonly ? true : null"
              [attr.disabled]="attrDisabled"
              [attr.maxlength]="maxLength ? maxLength : null"
              [attr.data-align]="textAlign"
              [attr.data-text-color]="color"
              [attr.autocomplete]="autoComplete"
              [attr.data-size]="size"
              [attr.data-size-tablet]="sizeTablet"
              [attr.data-size-desktop]="sizeDesktop"
              [attr.data-left-icon-size]="leftIconSize"
              [attr.data-right-icon-size]="rightIconSize"
              [attr.aria-errormessage]="errorId"
              [attr.aria-invalid]="hasError && !isWarning"
              [attr.aria-label]="label"
              [attr.data-borderless]="borderless"
              [attr.data-borderlessMobile]="borderlessMobile"
              [attr.data-weight]="fontWeight"
              [attr.data-edit-inline-title-size]="
                isText && editInlineTitleSize ? editInlineTitleSize : null
              "
              [attr.data-is-shadowed]="isShadowed"
              [attr.min]="minValue"
              [formControl]="control"
              [attr.data-background-color]="backgroundColor"
              [attr.data-transparent-background]="transparentBackground"
              [attr.data-edit-inline]="editInline"
              [attr.inputmode]="keyboardType"
              (focus)="handleFocus()"
              (blur)="handleBlur()"
              (keyup)="handleKeyUp()"
              (wheel)="handleInputWheel()"
              (change)="handleOnChange()"
            />
          }
        </div>
        @if (iconEndClickable) {
          <fl-button
            class="InputButtonEnd"
            [label]="iconEndLabel"
            (click)="handleIconEndClick($event)"
          >
            @if (iconEnd) {
              <fl-icon
                [size]="rightIconSize"
                [color]="rightIconColor"
                [hoverColor]="HoverColor.PRIMARY"
                [name]="iconEnd"
                [useIconFont]="true"
                [fillIconFont]="true"
              ></fl-icon>
            }
          </fl-button>
        }
        @if (iconEnd && !iconEndClickable) {
          <fl-icon
            class="IconElementEnd"
            [size]="rightIconSize"
            [color]="rightIconColor"
            [label]="iconEndLabel"
            [name]="iconEnd"
            [useIconFont]="true"
            [fillIconFont]="true"
          ></fl-icon>
        }
        @if (afterLabel) {
          <div
            class="InputLabel AfterLabel"
            [attr.data-size]="size"
          >
            <span class="LabelText"> {{ afterLabel }} </span>
          </div>
        }
        @if (
          isNumberInput &&
          showIncrement &&
          (type === InputType.NUMBER_TEXT || type === InputType.NUMBER)
        ) {
          <div class="InputIncrementContainer mr-xxsmall">
            <fl-icon
              class="InputIncrementContainer-button"
              [label]="'Increment'"
              [name]="'ui-chevron-up'"
              [size]="IconSize.SMALL"
              [useIconFont]="true"
              [fillIconFont]="true"
              [weight]="FontWeight.MEDIUM"
              (click)="handleAdd()"
            ></fl-icon>
            <fl-icon
              class="InputIncrementContainer-button Decrement"
              [name]="'ui-chevron-down'"
              [label]="'Decrement'"
              [size]="IconSize.SMALL"
              [useIconFont]="true"
              [fillIconFont]="true"
              [weight]="FontWeight.MEDIUM"
              (click)="handleMinus()"
            ></fl-icon>
          </div>
        }
        <ng-content select="fl-button"></ng-content>
      </div>
      @if (editInline) {
        <label
          class="LabelTag"
          for="id"
          [class.HasValue]="
            (isStringControl(control) && control.value.length !== 0) ||
            (isNumberControl(control) && isDefined(control.value))
          "
          [class.BeforeLabelSpace]="beforeLabel"
          [class.HideLabelTag]="hideLabelTag"
          [class.TitleInput]="isText && editInlineTitleSize"
          [class.IsInvalid]="hasError && !isWarning"
          [class.IsValid]="isValid === true"
          [attr.data-edit-inline-title-size]="
            isText && editInlineTitleSize ? editInlineTitleSize : null
          "
          [attr.data-background-color]="backgroundColor"
        >
          {{ labelTag }}
        </label>
      }
      @if (maxCharacter !== undefined && !hasError) {
        <p
          class="Counter text-body-16 {{
            remainingCharCount >= 0 ? 'text-foreground' : 'text-error'
          }}"
          [class.IsFocus]="inputFocused"
        >
          @if (remainingCharCount >= 0) {
            <ng-container i18n="Input field characters left text">
              {{ remainingCharCount }} characters left
            </ng-container>
          }
          @if (remainingCharCount < 0) {
            <ng-container i18n="Input field characters over limit text">
              {{ -remainingCharCount }} characters over limit
            </ng-container>
          }
        </p>
      }
      @if (isNumberIncrement) {
        <fl-button
          i18n="Input increment button"
          class="InputIncrement {{ hasError ? 'mb-xxxsmall' : 'mb-none' }}"
          [color]="ButtonColor.CUSTOM"
          [disabled]="attrDisabled === true || attrReadonly === true"
          [size]="buttonIncrementSize"
          [attr.data-invalid]="hasError && !isWarning"
          [attr.data-valid]="isValid"
          [attr.data-warning]="isWarning"
          (click)="handleAdd()"
        >
          <p>+</p>
        </fl-button>
      }
    </div>
    @if (hint && inputFocused && !isErrorDisplayed && !isValid) {
      <p class="Hint">
        {{ hint }}
      </p>
    }
    @if (displayValidation) {
      <fl-validation-error
        class="ValidationError"
        [id]="errorId"
        [attr.data-align]="align"
        [control]="control"
        [isWarning]="isWarning"
        [showIcon]="false"
        [customValidationError]="customValidationError"
      >
      </fl-validation-error>
    }
  `,
  styleUrls: ['./input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [shakeAnimation],
})
export class InputComponent<T> extends InputBaseComponent<T> {
  /**
   * Which kind of field to render. The allowed values depend on the value type
   * of `control` — a `FormControl<number>` accepts the number types, a
   * `FormControl<string>` the text ones. For dates, use `fl-date-input`.
   */
  // FIXME: T241170 - Remove the IsAny check once all forms are typed.
  // Currently used to defer fixing untyped FormControl.
  @Input() textSize?: InputTextSize;
  @Input() override type: IsAny<T> extends true
    ? any
    : // Accepts `undefined` as a default value, but internally, it
      // should never set `control.value` to `undefined`
      [T] extends [number] | [number | undefined]
      ? InputType.NUMBER | InputType.NUMBER_INCREMENT
      : [T] extends [string]
        ?
            | InputType.CURRENCY
            | InputType.EMAIL
            | InputType.NUMBER_TEXT
            | InputType.PASSWORD
            | InputType.TEXT
            | InputType.SEARCH
        : never;
}
